import { Button } from "@/components/ui/button";
import useRosterStatus from "@/hooks/zustand/useRosterStatus";
import { useQueryClient } from "@tanstack/react-query";
import { Grid } from "ldrs/react";
import "ldrs/react/Grid.css";
import { dismissRosterToast } from "./rosterToast";
import { CheckCircle2 } from "lucide-react";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

export default function RosterIndicater({
  resetGenerateRoster,
  generatedVersion,
}: {
  resetGenerateRoster: () => void;
  generatedVersion?: number | null;
}) {
  const queryClient = useQueryClient();
  const { rosterMade, resetRosterMadeState, setDismissedJobId } =
    useRosterStatus();
  const generatedVersionText =
    generatedVersion != null
      ? `생성된 근무표 ver ${generatedVersion}`
      : "생성된 근무표를 최신 버전으로 불러오는 중입니다.";

  return (
    <>
      {(rosterMade.status === "QUEUED" ||
        rosterMade.status === "RUNNING" ||
        rosterMade.status === "SUCCESS") && (
        <div className="rosterCreating">
          <div className="flex flex-col items-center justify-center gap-6">
            {rosterMade.status === "QUEUED" ||
            rosterMade.status === "RUNNING" ? (
              <>
                <p className="text-white text-3xl font-jalnan">
                  {rosterMade.status === "QUEUED" ? (
                    <>
                      근무표{" "}
                      <span className="bg-gradient-to-r from-[#71dafd] to-purple-300 bg-clip-text text-transparent">
                        대기
                      </span>{" "}
                      중입니다
                    </>
                  ) : (
                    <>
                      근무표{" "}
                      <span className="bg-gradient-to-r from-[#71dafd] to-purple-300 bg-clip-text text-transparent">
                        생성
                      </span>{" "}
                      중입니다
                    </>
                  )}
                </p>
                <p className="text-background text-base ">
                  {rosterMade.status === "QUEUED" && "곧 생성을 시작합니다..."}

                  {rosterMade.status === "RUNNING" && (
                    <span className="animate-pulse">
                      최적의 근무 조합을 계산하고 있습니다.
                    </span>
                  )}
                </p>
                {/* ldrs Grid는 단색만 지원 — 블러 그라데이션 글로우로 톤 맞춤 */}
                <div className="relative flex h-[120px] w-[120px] items-center justify-center">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-[-28%] rounded-full bg-gradient-to-br from-[#21266b] via-[#a855f7] to-[#e7ecf7] opacity-[0.55] blur-2xl motion-safe:animate-pulse"
                  />{" "}
                  <Grid size="120" speed="1.5" color="white" />
                  <div className="relative z-10 py-10 drop-shadow-[0_0_14px_rgba(7,172,227,0.35)]"></div>
                </div>
                <div className="flex flex-col items-center gap-1 mt-4">
                  <p className="text-white/80 text-base font-light ">
                    * 생성 중 새로고침 시 데이터가 유실될 수 있으니
                    주의해주세요.
                  </p>
                </div>
              </>
            ) : rosterMade.status === "SUCCESS" ? (
              <div className="flex flex-col items-center gap-4">
                <>
                  <p className="text-white text-3xl font-jalnan">
                    <>
                      근무표가{" "}
                      <span className="bg-gradient-to-r from-[#71dafd] to-purple-300 bg-clip-text text-transparent">
                        생성
                      </span>{" "}
                      되었습니다
                    </>
                  </p>
                  {/* ldrs Grid는 단색만 지원 — 블러 그라데이션 글로우로 톤 맞춤 */}
                  <div className="relative flex h-[120px] w-[120px] items-center justify-center">
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-[-28%] rounded-full bg-gradient-to-br from-[#21266b] via-[#a855f7] to-[#e7ecf7] opacity-[0.55] blur-2xl motion-safe:animate-pulse"
                    />{" "}
                    {/* <Grid size="120" speed="1.5" color="white" /> */}
                    <CheckCircle2 className="size-16 text-white z-10" />
                    <div className="relative z-10 py-10 drop-shadow-[0_0_14px_rgba(7,172,227,0.35)]"></div>
                  </div>{" "}
                  <p className="text-white/80 text-base font-light ">
                    {generatedVersionText}
                  </p>
                </>
                <div className="mt-5">
                  <Button
                    type="button"
                    className=" bg-gradient-to-r from-[#07ACE3] to-purple-500 text-white hover:bg-gradient-to-r hover:from-[#07ACE3]/80 hover:to-purple-500/80 py-7! px-12 rounded-full!"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const jobStatusQueryKey = QUERY_KEYS.roster.jobStatus();
                      await queryClient.cancelQueries({
                        queryKey: jobStatusQueryKey,
                      });
                      const jobStatus = queryClient.getQueryData<{
                        job_id?: unknown;
                      }>(jobStatusQueryKey);
                      if (typeof jobStatus?.job_id === "string") {
                        setDismissedJobId(jobStatus.job_id);
                      }
                      queryClient.setQueryData(jobStatusQueryKey, null);
                      queryClient.removeQueries({
                        queryKey: jobStatusQueryKey,
                      });
                      resetRosterMadeState();
                      resetGenerateRoster();
                      dismissRosterToast();
                    }}
                  >
                    확인
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
